import unified from 'unified';
import remarkParse from 'remark-parse';

type MdastNode = {
  type: string;
  value?: string;
  url?: string;
  alt?: string | null;
  depth?: number;
  ordered?: boolean;
  start?: number | null;
  lang?: string | null;
  children?: MdastNode[];
};

// Html コンポーネントのサニタイズ設定 (allowedSchemesByTag) と揃えている。
// data: が必要なのは埋め込み画像だけで、リンク先としての用途はない
const SafeLinkProtocols = ['http:', 'https:'];
const SafeImageProtocols = ['http:', 'https:', 'data:'];

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function isSafeUrl(url: string, allowedProtocols: readonly string[]): boolean {
  try {
    return allowedProtocols.includes(new URL(url, 'https://example.com').protocol);
  } catch {
    return false;
  }
}

function serializeChildren(node: MdastNode): string {
  return (node.children ?? []).map(serialize).join('');
}

function serialize(node: MdastNode): string {
  switch (node.type) {
    case 'paragraph':
      return `<p>${serializeChildren(node)}</p>`;
    case 'heading': {
      const depth = Math.min(Math.max(node.depth ?? 1, 1), 6);
      return `<h${depth}>${serializeChildren(node)}</h${depth}>`;
    }
    case 'thematicBreak':
      return '<hr>';
    case 'blockquote':
      return `<blockquote>${serializeChildren(node)}</blockquote>`;
    case 'list': {
      if (!node.ordered) return `<ul>${serializeChildren(node)}</ul>`;
      const start = node.start ?? 1;
      return `<ol${start === 1 ? '' : ` start="${start}"`}>${serializeChildren(node)}</ol>`;
    }
    case 'listItem':
      return `<li>${serializeChildren(node)}</li>`;
    case 'code': {
      const className = node.lang ? ` class="language-${escapeHtml(node.lang)}"` : '';
      return `<pre><code${className}>${escapeHtml(node.value ?? '')}</code></pre>`;
    }
    case 'inlineCode':
      return `<code>${escapeHtml(node.value ?? '')}</code>`;
    case 'emphasis':
      return `<em>${serializeChildren(node)}</em>`;
    case 'strong':
      return `<strong>${serializeChildren(node)}</strong>`;
    case 'break':
      return '<br>';
    case 'text':
      return escapeHtml(node.value ?? '');
    case 'link': {
      const url = node.url ?? '';
      if (!isSafeUrl(url, SafeLinkProtocols)) return serializeChildren(node);
      return `<a href="${escapeHtml(url)}">${serializeChildren(node)}</a>`;
    }
    case 'image': {
      const url = node.url ?? '';
      if (!isSafeUrl(url, SafeImageProtocols)) return '';
      return `<img src="${escapeHtml(url)}" alt="${escapeHtml(node.alt ?? '')}">`;
    }
    // Markdown 中の生 HTML は TipTap で扱えるノードに対応づけられないため破棄する
    case 'html':
      return '';
    default:
      return serializeChildren(node);
  }
}

/**
 * Markdown を説明パネル用の HTML に変換します。
 *
 * CommonMark として解釈し、説明パネルのエディタ (TipTap: StarterKit + Link + Image) に
 * 対応づくノードのみを出力します。それ以外の記法はマークアップを捨てて中身のテキストだけを残します。
 * GFM 拡張 (表・タスクリスト・打ち消し線) は解釈しないため、打ち消し線はエディタ側が対応していても
 * ~~ を含む文字列のまま出力されます。
 */
export function markdownToHtml(markdown: string): string {
  const tree: MdastNode = unified().use(remarkParse).parse(markdown);
  const html = serializeChildren(tree);

  // 内容全体が生HTMLのような非対応記法だけで構成されていると出力が空になり、
  // 何も表示されないパネルができてしまう。読める形でテキストとして残す。
  if (html === '' && markdown.trim() !== '') {
    return `<p>${escapeHtml(markdown.trim()).replace(/\n/g, '<br>')}</p>`;
  }

  return html;
}
